import { useMemo, type ComponentType, type ReactElement, type ReactNode } from 'react'

import type { TypePageEntry } from '@contentful-types/index'

import type { PageSwoosh } from '@constants/enums/swoosh'

import { PlacementContext } from '@contexts/placement-context'
import type { IPlacementContext } from '@contexts/placement-context'
import { SectionContext } from '@contexts/section-context'

import type { GenericContentRendererProps } from '../types'

function Section({
  isHero,
  isH1Section,
  indexInPage,
  isGrouped,
  hasGroupHeading,
  data,
  sectionGroupSwoosh,
  isTrustedBySection,
  children,
}: {
  isHero?: boolean
  isH1Section?: boolean
  indexInPage?: number
  isGrouped?: boolean
  hasGroupHeading?: boolean
  sectionGroupSwoosh?: PageSwoosh
  data: NonNullable<TypePageEntry['fields']['sections']>[number]
  isTrustedBySection?: boolean
  children?: ReactNode
}): ReactElement {
  const { internalName } = data?.fields || {}
  const placementContext = useMemo<IPlacementContext>(
    () => ({ placement: 'Section', placementInternalName: internalName }),
    [internalName],
  )

  return (
    <SectionContext.Provider
      value={{
        isHero,
        isH1Section,
        indexInPage,
        isGrouped,
        hasGroupHeading,
        sectionGroupSwoosh,
        isTrustedBySection,
      }}
    >
      <PlacementContext.Provider value={placementContext}>{children}</PlacementContext.Provider>
    </SectionContext.Provider>
  )
}

interface SectionRendererProps {
  isHero?: boolean
  isH1Section?: boolean
  isGrouped?: boolean
  hasGroupHeading?: boolean
  sectionGroupSwoosh?: PageSwoosh
  data?:
    | TypePageEntry['fields']['sections']
    | NonNullable<TypePageEntry['fields']['sections']>[number]
  startIndexInPage?: number
  isTrustedBySection?: boolean
}

export const buildSectionRenderer = (
  ContentRenderer: ComponentType<GenericContentRendererProps>,
): ComponentType<SectionRendererProps> => {
  const SectionRenderer = (props: SectionRendererProps) => {
    const { data, startIndexInPage, ...rest } = props

    if (!data) {
      return null
    }

    return Array.isArray(data) ? (
      data.map((s, i) => {
        // Guard if the linked entity is broken
        if (!s?.sys?.contentType?.sys) {
          return null
        }
        let nextStartIndexInPage
        if (startIndexInPage !== undefined) {
          nextStartIndexInPage = rest.isGrouped ? startIndexInPage : startIndexInPage + i
        }

        return (
          <SectionRenderer
            key={`${s.sys.contentType.sys.id}-${s.sys.id}`}
            {...rest}
            startIndexInPage={nextStartIndexInPage}
            data={s}
          />
        )
      })
    ) : (
      <Section {...rest} data={data} indexInPage={startIndexInPage}>
        <ContentRenderer data={data} />
      </Section>
    )
  }

  return SectionRenderer
}
